import { Injectable } from '@angular/core';
import { ModalOptions, ModalService } from '@healthcatalyst/cashmere';
import { OktaAuthService } from 'src/app/core/services/http/okta-auth.service';
import { environment } from 'src/environments/environment';
import { DbxSignInModalComponent } from '../components/dbx-sign-in-modal/dbx-sign-in-modal.component';
import { WindowService } from 'src/app/modules/data/data-entry/services/window.service';
import { OktaUser } from '../models/okta-user';
import { hashString } from '../utils/hash-string';
import { shareReplay, tap } from 'rxjs';
import { dbxSessionStorageKey } from '../constants/constants';
import { FeatureToggleService } from 'src/app/core/services/application/feature-toggle.service';
import { FeatureFlag } from '../constants/feature-flags';
import { TenantContextService } from '../tenant-context/tenant-context.service';

@Injectable({
    providedIn: 'root'
})
export class DatabricksLoginCheckService {
    oktaUser: OktaUser;
    environmentCode: string = environment.environmentCode.toLocaleLowerCase();

    constructor(
        private modalService: ModalService,
        private window: WindowService,
        private featureToggleService: FeatureToggleService,
        private tenantContext: TenantContextService,
        oktaAuthService?: OktaAuthService
    ) {
        if (oktaAuthService) {
            oktaAuthService.getUser().then(user => (this.oktaUser = user));
        }
    }

    public isBaseUrl(url: string): boolean {
        try {
            const parsed = new URL(url);
            return (
                !parsed.pathname ||
                (parsed.pathname === '/aad/auth' && !parsed.search) ||
                this.isAccountsHost(parsed.hostname)
            );
        } catch {
            return false;
        }
    }

    public isDatabricksUrl(url: string): boolean {
        try {
            const host = new URL(url).hostname.toLowerCase();
            return (
                this.isHostOrSubdomain(host, 'azuredatabricks.net') ||
                this.isHostOrSubdomain(host, 'azure.databricksapps.com')
            );
        } catch {
            return false;
        }
    }

    async showDbxAuthModal(url: string): Promise<boolean> {
        const isModifiedDbxLinkEnabled = this.featureToggleService.getFeatureFlagsByName(
            FeatureFlag.UseModifiedDBXLink
        ).enabled;

        if (!isModifiedDbxLinkEnabled) {
            return false;
        }

        if (this.environmentCode === 'prod') {
            return (
                this.tenantContext &&
                this.tenantContext.currentTenant?.tenantId.toLowerCase() === 'ixhc' &&
                !(await this.isDatabricksHostStored(url))
            );
        }

        return !(await this.isDatabricksHostStored(url));
    }

    buildDbxLoginUrl(url: string): string {
        try {
            const parsed = new URL(url);
            if (!parsed.pathname.startsWith('/aad/auth')) {
                parsed.pathname = '/aad/auth';
            }

            parsed.searchParams.set('login_hint', this.getResolvedLoginHint());
            return parsed.toString();
        } catch {
            return url;
        }
    }

    restoreTargetUrl(url: string): string {
        return url.replace('/aad/auth', '');
    }

    async isDatabricksHostStored(url: string): Promise<boolean> {
        const host = this.getHost(url);

        if (!host) {
            return false;
        }

        const hashed = await hashString(host);

        const stored = sessionStorage.getItem(dbxSessionStorageKey);
        if (stored) {
            const hosts = this.parseStoredHosts(stored);
            return hosts.includes(hashed);
        }
        return false;
    }

    async addDatabricksHostToStorage(url: string): Promise<void> {
        const host = this.getHost(url);

        if (!host) {
            return;
        }

        const hashed = await hashString(host);

        let hosts: string[] = [];
        const stored = sessionStorage.getItem(dbxSessionStorageKey);

        if (stored) {
            hosts = this.parseStoredHosts(stored);
        }

        if (!hosts.includes(hashed)) {
            hosts.push(hashed);
            sessionStorage.setItem(dbxSessionStorageKey, JSON.stringify(hosts));
        }
    }

    async handleBaseUrl(url: string): Promise<void> {
        if (this.isAccountsHost(this.getHostname(url))) {
            this.window.open(url);
        } else {
            const loginUrl = this.buildDbxLoginUrl(url);
            await this.addDatabricksHostToStorage(loginUrl);
            this.window.open(loginUrl);
        }
    }

    promptDbxLoginForExternalNavigation(url: string) {
        const { loginUrl, result } = this.openDbxLoginModal(url);
        return result.pipe(
            tap(() => {
                this.addDatabricksHostToStorage(loginUrl);
                this.window.open(this.getSanitizedTargetDbxUrl(url));
            }),
            shareReplay(1)
        );
    }

    promptDbxLoginForEmbeddedNavigation(dbxUrl: string) {
        const { result } = this.openDbxLoginModal(dbxUrl);
        return result;
    }

    private openDbxLoginModal(dbxUrl: string) {
        const loginUrl = this.buildDbxLoginUrl(dbxUrl);
        const modalOptions: ModalOptions = {
            ignoreEscapeKey: true,
            ignoreOverlayClick: true,
            size: 'lg',
            data: { url: loginUrl }
        };
        const modalRef = this.modalService.open(DbxSignInModalComponent, modalOptions);
        return {
            loginUrl,
            result: modalRef.result
        };
    }

    getSanitizedTargetDbxUrl(url: string): string {
        const sanitizedUrl = url.includes('aad/auth') ? this.restoreTargetUrl(url) : url;
        return this.normalizeDbxTargetUrl(sanitizedUrl);
    }

    public getLaunchUrl(url: string): string {
        if (!url || !this.isDatabricksUrl(url)) {
            return url;
        }

        return this.getSanitizedTargetDbxUrl(url);
    }

    private normalizeDbxTargetUrl(url: string): string {
        try {
            const parsed = new URL(url);
            parsed.searchParams.set('login_hint', this.getResolvedLoginHint());
            return parsed.toString();
        } catch {
            return url;
        }
    }

    private getResolvedLoginHint(): string {
        return this.oktaUser?.azureUpn || this.oktaUser?.username || '@healthcatalystcloud.onmicrosoft.com';
    }

    private parseStoredHosts(storedHosts: string): string[] {
        try {
            const parsedHosts: unknown = JSON.parse(storedHosts);

            if (!Array.isArray(parsedHosts) || !parsedHosts.every(host => typeof host === 'string')) {
                return [];
            }

            return parsedHosts;
        } catch {
            return [];
        }
    }

    private getHost(url: string): string {
        try {
            const parsed = new URL(url);
            return parsed.host;
        } catch {
            console.warn('Invalid URL passed to DatabricksLoginCheckService:', url);
            return '';
        }
    }

    private getHostname(url: string): string {
        try {
            return new URL(url).hostname.toLowerCase();
        } catch {
            return '';
        }
    }

    private isAccountsHost(hostname: string): boolean {
        return hostname === 'accounts.azuredatabricks.net';
    }

    private isHostOrSubdomain(hostname: string, domain: string): boolean {
        return hostname === domain || hostname.endsWith(`.${domain}`);
    }
}
